'use client';

import { Globe } from 'lucide-react';
import { countries } from '@/lib/data/countries';
import { useTranslations } from 'next-intl';

interface CountrySelectProps {
    id?: string;
    name: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    required?: boolean;
    className?: string;
    placeholder?: string;
    label?: string;
    showLabel?: boolean;
}

export function CountrySelect({
    id,
    name,
    value,
    onChange,
    required = false,
    className = '',
    placeholder,
    label,
    showLabel = false,
}: CountrySelectProps) {
    const t = useTranslations('form');

    const defaultPlaceholder = placeholder || t('selectCountry') || 'Select your country';
    const selectId = id || name;
    const labelText = label || t('countryLabel') || 'Country';

    return (
        <div className="group relative">
            {/* Label - visually hidden by default for accessibility, visible if showLabel is true */}
            <label
                htmlFor={selectId}
                className={showLabel 
                    ? "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" 
                    : "sr-only"
                }
            >
                {labelText}
                {required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="absolute left-4 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
                <Globe className="w-5 h-5 text-gray-400 dark:text-gray-500 group-focus-within:text-primary-500 transition-colors duration-300" aria-hidden="true" />
            </div>
            <select
                id={selectId}
                name={name}
                value={value || ''}
                onChange={onChange}
                required={required}
                aria-label={labelText}
                aria-required={required}
                className={`w-full pl-12 pr-4 py-4 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm border-2 border-gray-200/50 dark:border-gray-700/50 rounded-lg text-gray-900 dark:text-white focus:border-primary-500 dark:focus:border-primary-400 focus:bg-white dark:focus:bg-gray-900 focus:shadow-lg focus:shadow-primary-500/20 focus:outline-none transition-all duration-300 appearance-none cursor-pointer ${className}`}
            >
                {countries.map((country) => {
                    const isPlaceholder = country === 'Select a country';
                    const label = isPlaceholder ? defaultPlaceholder : country;
                    const value = isPlaceholder ? '' : country;
                    return (
                        <option
                            key={country}
                            value={value}
                            className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                        >
                            {label}
                        </option>
                    );
                })}
            </select>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
                <svg
                    className="w-5 h-5 text-gray-400 dark:text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                    />
                </svg>
            </div>
            <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary-500/0 via-primary-500/0 to-primary-500/0 group-focus-within:from-primary-500/10 group-focus-within:via-primary-500/5 group-focus-within:to-primary-500/10 transition-all duration-300 pointer-events-none" />
        </div>
    );
}

